/**
 * ChangelogView — paginated list of `update_log` rows.
 *
 * Initial fetch asks for 50; the "更多" button (handled by the
 * pagination component) requests 200 (server cap). Each row shows:
 *
 *   ts  | kind badge | message | week_id / epdat | lastError (if any)
 *
 * Errors render inside a `<pre>` block so multi-line stack traces
 * stay readable.
 */

import { el, setChildren } from "../dom";
import { ApiError, fetchChangelog } from "../api";
import { setCacheEntry, getStore } from "../store";
import { pagination } from "../components/pagination";
import type { ChangelogRow, LogKind } from "../types";

export function renderChangelogView(): HTMLElement {
  let currentCap = 50;
  const root = el("section", { class: "view view-changelog", "data-view": "changelog" }, [
    el("div", { class: "view-title" }, [
      el("h1", null, "更新日志"),
      el("p", { class: "view-title-sub" }, "正在加载…"),
    ]),
    el("div", { class: "status-row" }, [
      el("div", { class: "spinner", "aria-hidden": "true" }),
      el("div", { class: "status-row-title" }, "正在加载…"),
    ]),
  ]);

  void load(currentCap);
  return root;

  async function load(limit: number): Promise<void> {
    if (limit === 50) {
      const cached = getStore().cache.changelog;
      if (cached) {
        paint(cached, limit);
        return;
      }
    }

    try {
      const rows = await fetchChangelog(limit);
      // Cache only the initial 50-row fetch — when the user clicks
      // "更多" the larger list is transient.
      if (limit === 50) setCacheEntry("changelog", rows);
      paint(rows, limit);
    } catch (err) {
      const error = err instanceof ApiError ? err : null;
      setChildren(root, [
        el("div", { class: "view-title" }, [
          el("h1", null, "更新日志"),
          el("p", { class: "view-title-sub" }, "无法加载"),
        ]),
        el("div", { class: "status-row is-error" }, [
          el("div", { class: "status-row-title" }, "无法加载"),
          el(
            "div",
            { class: "status-row-detail" },
            error ? `${error.status} · ${error.message}` : "网络错误，请稍后重试。",
          ),
        ]),
      ]);
    }
  }

  function paint(rows: ChangelogRow[], cap: number): void {
    currentCap = cap;
    const body = el("div", { class: "main-pane view-body" });

    if (rows.length === 0) {
      setChildren(root, [
        el("div", { class: "view-title" }, [
          el("h1", null, "更新日志"),
          el("p", { class: "view-title-sub" }, "暂无运行记录"),
        ]),
        el("div", { class: "status-row" }, [
          el("div", { class: "status-row-title" }, "暂无内容"),
          el(
            "div",
            { class: "status-row-detail" },
            "更新日志为空。请等待每日 / 每周工作流运行，或手动触发一次。",
          ),
        ]),
      ]);
      return;
    }

    const list = el("div", { class: "changelog-list" });
    for (const r of rows) {
      list.appendChild(renderRow(r));
    }

    const pager = pagination({
      shown: rows.length,
      cap,
      onMore: () => {
        currentCap = 200;
        void load(200);
      },
      onRefresh: () => {
        // Drop cached entry so the refresh re-fetches.
        const cur = getStore().cache.changelog;
        if (cur && currentCap === 50) {
          // No-op: the cache is already valid for the next mount.
        }
        void load(currentCap);
      },
    });

    body.appendChild(list);
    body.appendChild(pager);

    setChildren(root, [
      el("div", { class: "view-title" }, [
        el("h1", null, "更新日志"),
        el(
          "p",
          { class: "view-title-sub" },
          `展示每日 / 每周工作流的最近运行记录 · 当前 ${rows.length} 条`,
        ),
      ]),
      body,
    ]);
  }

  function renderRow(r: ChangelogRow): HTMLElement {
    return el("article", { class: "changelog-row" }, [
      el("header", { class: "changelog-row-head" }, [
        el("span", { class: `changelog-kind is-${r.kind}` }, kindLabel(r.kind)),
        el("time", { class: "changelog-ts" }, formatTs(r.ts)),
        r.week_id
          ? el("span", { class: "changelog-scope" }, `week ${r.week_id}`)
          : null,
        r.epdat
          ? el("span", { class: "changelog-scope" }, `epdat ${r.epdat}`)
          : null,
      ]),
      el("p", { class: "changelog-message" }, r.message || "（无消息）"),
    ]);
  }
}

function kindLabel(k: LogKind): string {
  switch (k) {
    case "daily":
      return "每日";
    case "weekly":
      return "每周";
    case "manual":
      return "手动";
    case "error":
      return "错误";
    default:
      return k;
  }
}

function formatTs(ts: string): string {
  // The server returns ISO-like `2026-07-26 00:00:01`. Pass through if
  // it looks already friendly.
  if (ts.includes("T")) return ts.replace("T", " ").replace(/\..+$/u, "");
  return ts;
}